/**
 * useBeforeUnload Hook - Prevencao de Perda de Dados
 * @module features/chat/hooks/useBeforeUnload
 *
 * Hook para gerenciar aviso de fechamento de aba durante conversa ativa.
 * Previne perda acidental de dados do assessment.
 *
 * T003-FE: Persistencia de Sessao e Prevencao de Perda de Dados
 *
 * Features:
 * - Registra evento beforeunload quando ativo
 * - Remove listener ao desmontar
 * - Verifica se ha mensagens antes de avisar
 * - Nao avisa se assessment estiver completo
 */

import { useCallback, useEffect, useMemo } from 'react';
import { isSessionComplete, useSessionStore } from '../stores/sessionStore';

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Mensagem padrao exibida no dialog de confirmacao
 */
const DEFAULT_WARNING_MESSAGE = 'Voce tem alteracoes nao salvas. Deseja realmente sair?';

// =============================================================================
// TYPES
// =============================================================================

export interface UseBeforeUnloadOptions {
  /**
   * Habilita ou desabilita o hook
   * @default true
   */
  enabled?: boolean;

  /**
   * Mensagem customizada para exibir no dialog
   * @default 'Voce tem alteracoes nao salvas. Deseja realmente sair?'
   */
  message?: string;
}

export interface UseBeforeUnloadReturn {
  /**
   * Indica se o aviso esta ativo (enabled + hasMessages + !completed)
   */
  isActive: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook para gerenciar aviso de fechamento de aba
 *
 * Registra um listener de beforeunload quando:
 * - enabled = true
 * - Existem mensagens no chat
 * - A sessao NAO esta completa
 *
 * @param options - Opcoes de configuracao
 * @returns Estado do hook
 *
 * @example
 * ```tsx
 * function ChatPage() {
 *   const { isActive } = useBeforeUnload({ enabled: true });
 *
 *   return (
 *     <div>
 *       {isActive && <span>Protecao ativa</span>}
 *       ...
 *     </div>
 *   );
 * }
 * ```
 */
export function useBeforeUnload(options: UseBeforeUnloadOptions = {}): UseBeforeUnloadReturn {
  const { enabled = true, message = DEFAULT_WARNING_MESSAGE } = options;

  // Store selectors
  let hasMessages = false;
  let isComplete = false;

  try {
    hasMessages = useSessionStore((state) => state.hasMessages());
    isComplete = useSessionStore(isSessionComplete);
  } catch {
    // Handle store errors gracefully
    hasMessages = false;
    isComplete = false;
  }

  /**
   * Determina se o aviso deve estar ativo
   */
  const isActive = useMemo(() => {
    return enabled && hasMessages && !isComplete;
  }, [enabled, hasMessages, isComplete]);

  /**
   * Handler do evento beforeunload
   */
  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isActive) {
        return;
      }

      // Padrao moderno para prevenir fechamento
      event.preventDefault();

      // Para compatibilidade com navegadores antigos
      event.returnValue = message;

      return message;
    },
    [isActive, message]
  );

  /**
   * Efeito para registrar/remover listener
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, handleBeforeUnload]);

  return {
    isActive,
  };
}

export default useBeforeUnload;
